/**
 * Knowledge Views — the primary content model for the Cloud Knowledge
 * Workspace. These types mirror the control-plane contracts produced by the
 * unified SYNTHESIZE → NARRATE pipeline (`cloudox/reporting/views/`,
 * `cloudox/knowledge_views/`, `cloudox/quality/`):
 *
 *  - `GET /v1/runs/{id}/views`   → {@link RawViewNavigation}  (kind: knowledge-view-navigation)
 *  - `GET /v1/runs/{id}/quality` → {@link RawQualityManifest} (quality/index.json)
 *  - `GET /v1/runs/{id}/documents/{view}` → narrated Markdown per view
 *
 * The API returns these as opaque JSON; the adapters in this folder validate
 * and normalise them into the shapes the UI renders, defending against missing
 * fields so older / partially-generated runs still display.
 */

/**
 * Canonical audience lenses, plus `reference` — the deterministic technical
 * Reference Layer. `reference` is **not** an AI-narrated audience view; it is a
 * graph-derived set of inventory/relationship tables surfaced in the same
 * picker (rendered last, behind a divider). Kept open-ended for forward
 * compatibility.
 */
export type KnowledgeViewKey =
  | "generic"
  | "executive"
  | "architect"
  | "operations"
  | "security"
  | "finops"
  | "reference";

/**
 * Stable display order for the view picker: the default `generic` first, the
 * audience lenses next, and the deterministic `reference` layer last.
 */
export const KNOWLEDGE_VIEW_ORDER: readonly KnowledgeViewKey[] = [
  "generic",
  "executive",
  "architect",
  "operations",
  "security",
  "finops",
  "reference",
];

/** The deterministic Reference Layer's key — surfaced apart from audience views. */
export const REFERENCE_VIEW_KEY: KnowledgeViewKey = "reference";

/** User-facing confidence label (projected from the internal high/medium/low/unknown). */
export type UserConfidence = "Verified" | "Likely" | "Assumed" | "Unknown";

export const CONFIDENCE_ORDER: readonly UserConfidence[] = [
  "Verified",
  "Likely",
  "Assumed",
  "Unknown",
];

/** Where a section sits in the understanding arc. */
export type SectionOrientation = "overview" | "detail" | "reference";

/**
 * Where a section's prose came from, in the backend's own vocabulary.
 *
 * Three states, and the distinction between the last two is the one that
 * matters to a reader:
 *
 * - `ai` — narrated by the model and accepted by semantic acceptance;
 * - `fallback` — deterministic prose, because narration was withheld (rejected,
 *   unavailable, or disabled). The section is still true, just plainer;
 * - `empty` — the section has no evidence to narrate. That is a finding about
 *   the environment, not a narration failure, so it must not be shown as one.
 *
 * The field is **absent** when the run did not narrate at all (a deterministic
 * run, or a nav manifest written before narration existed): nothing was
 * withheld, so nothing should be reported. An unrecognised value is also
 * dropped rather than guessed, so a future backend state can never be
 * mislabelled as accepted AI prose.
 */
export type NarrationSource = "ai" | "fallback" | "empty";

/** The `source` values this client understands, for narrowing unknown input. */
export const NARRATION_SOURCES: readonly NarrationSource[] = [
  "ai",
  "fallback",
  "empty",
];

/** Letter grade for a quality score. */
export type QualityGrade = "A" | "B" | "C" | "D" | "F";

// -- normalised navigation -------------------------------------------------

export interface KnowledgeSection {
  readonly key: string;
  readonly title: string;
  /** GitHub-style heading anchor within the rendered view document. */
  readonly anchor: string;
  readonly orientation: SectionOrientation;
  readonly confidence: UserConfidence;
  readonly empty: boolean;
  readonly entityCount: number;
  readonly subsections: readonly string[];
  /**
   * Coarse navigation group this section belongs to (e.g. "Overview",
   * "Risk & Operations"). Purely presentational — the sidebar clusters sections
   * sharing a group under a subtle category header. Empty = ungrouped.
   */
  readonly group?: string;
  /**
   * Curated, stakeholder-specific questions (only the "Key Questions Answered"
   * section; empty elsewhere). Carried for completeness / tooltips.
   */
  readonly questions: readonly string[];
  /** Standalone section file, relative to the report root (nav schema v2+). */
  readonly path?: string;
  /** Section position within the view (nav schema v2+). */
  readonly order?: number;
  /** Present only when the run narrated this view. See {@link NarrationSource}. */
  readonly source?: NarrationSource;
  readonly kqiScore?: number;
  readonly needsImprovement?: boolean;
  /** Per-section content hash (nav schema v2+); section-level change tracking. */
  readonly contentHash?: string;
  /**
   * How many pairwise change lines this section was given, and how many
   * multi-run temporal statements — stamped by the narration pipeline from the
   * deterministic projection, not from the prose.
   *
   * This is what lets a temporal surface link a reader to the sections that
   * actually discuss change instead of guessing which ones might.
   */
  readonly temporalChanges?: number;
  readonly temporalStatements?: number;
}

export interface KnowledgeView {
  readonly key: KnowledgeViewKey;
  readonly displayName: string;
  readonly audience: string;
  readonly depth: string;
  /** Per-view confidence display policy: minimal | summary | prominent. */
  readonly confidenceDisplay: "minimal" | "summary" | "prominent";
  readonly overallConfidence: UserConfidence;
  readonly sectionCount: number;
  readonly personaQuestions: readonly string[];
  /**
   * Composed view document, relative to the report root. Nav schema v2+ points
   * this at `views/<key>/README.md`; older single-file runs omit it (the viewer
   * falls back to the legacy `views/<key>.md` convention).
   */
  readonly outputRelpath?: string;
  readonly contentHash?: string;
  readonly aiSectionCount?: number;
  readonly fallbackSectionCount?: number;
  /** Sections with no evidence to narrate (an environment fact, not a failure). */
  readonly emptySectionCount?: number;
  readonly sections: readonly KnowledgeSection[];
  /** True when this view failed to build / narrate (carries the message). */
  readonly error?: string;
}

/**
 * A cross-cutting page written beside the `views/` tree — Knowledge Quality,
 * Cost Intelligence, Environment Evolution.
 *
 * The producer only lists a page it actually wrote, so a page present here is a
 * page that exists. Carried because the console previously dropped this array
 * entirely: the generated Knowledge Quality page was written on every run and
 * reachable from nothing, so the reader could not get to it at all.
 */
export interface RelatedPage {
  readonly key: string;
  readonly title: string;
  /** Report-root-relative path, which is also the document slug. */
  readonly path: string;
  readonly description: string;
}

export interface ViewNavigation {
  readonly schemaVersion: number;
  readonly provider: string;
  readonly generatedAt?: string;
  readonly narrated: boolean;
  readonly defaultView: KnowledgeViewKey;
  readonly views: readonly KnowledgeView[];
  /** Cross-cutting standalone pages this run produced (may be empty). */
  readonly relatedPages: readonly RelatedPage[];
  /**
   * Views the run could not build, keyed by view. A view that failed is absent
   * from `views`, so without this a reader cannot tell "your plan excludes it"
   * from "this run could not produce it".
   */
  readonly errors: Readonly<Record<string, string>>;
}

// -- normalised quality ----------------------------------------------------

export interface QualityDimensionCount {
  readonly dimension: string;
  readonly count: number;
}

/**
 * What became of one countable unit of assurance work. Mirrors the backend
 * `UnitDisposition` (`cloudox/contracts/coverage.py`).
 */
export type CoverageDisposition =
  | "passed"
  | "failed"
  | "skipped"
  | "not_applicable"
  | "unavailable"
  | "errored"
  | "not_assessed";

/** Mirrors the backend `AssessmentState` values a coverage projects onto. */
export type CoverageState =
  | "assessed_present"
  | "assessed_absent"
  | "partial"
  | "unsupported"
  | "unavailable"
  | "not_applicable"
  | "not_assessed";

/**
 * How much of a declared universe of assurance work actually happened.
 *
 * The backend serialises counts only — {@link coverageState} and
 * {@link coverageAssessedRatio} derive the rest, mirroring
 * `cloudox/contracts/coverage.py`. Read the derivation there before changing
 * it here: two implementations of "is this complete" that disagree is the
 * defect the primitive exists to prevent.
 */
export interface Coverage {
  readonly unit: string;
  readonly eligible: number;
  readonly passed: number;
  readonly failed: number;
  readonly skipped: number;
  readonly notApplicable: number;
  readonly unavailable: number;
  readonly errored: number;
  readonly notAssessed: number;
}

/** A ratio that cannot be read without its denominator; `null` over zero. */
export interface CoverageRatio {
  readonly numerator: number;
  readonly denominator: number;
  /** `numerator / denominator`, or `null` when there is nothing to divide by. */
  readonly value: number | null;
  readonly basis: string;
}

/** What KQI does and does not judge, as the artifact itself declares it. */
export interface QualityScope {
  readonly judges: readonly string[];
  readonly doesNotJudge: readonly string[];
  readonly note: string;
}

/** Per-view outcome; mirrors the backend `ViewOutcome`. */
export type QualityViewOutcome =
  | "scored"
  | "materially_weak"
  | "failed"
  | "missing";

export interface QualityViewSummary {
  readonly key: KnowledgeViewKey;
  readonly displayName: string;
  readonly outcome: QualityViewOutcome;
  /** `null` when the view could not be scored — never substituted with 0. */
  readonly overallScore: number | null;
  /** `null` when there is no score to grade. */
  readonly grade: QualityGrade | null;
  readonly personaCoverageRatio: number | null;
  readonly findingCount: number;
}

export interface QualitySummary {
  readonly schemaVersion: number;
  readonly provider: string;
  readonly generatedAt?: string;
  /**
   * The run's headline score, or `null` when view coverage is incomplete.
   *
   * The backend publishes this only when every expected view was scored, so a
   * `null` here means the run did not do the work — not that it scored zero.
   * {@link meanScoredViewScore} still carries the partial mean for a reader who
   * wants it, labelled as partial.
   */
  readonly overallScore: number | null;
  readonly grade: QualityGrade | null;
  /** Mean over the views that were scored. Present even when the headline is not. */
  readonly meanScoredViewScore: number | null;
  readonly weakestViewScore: number | null;
  readonly viewCoverage: Coverage;
  readonly scope: QualityScope;
  readonly strongestView?: KnowledgeViewKey;
  readonly weakestView?: KnowledgeViewKey;
  readonly findingCount: number;
  readonly countsByDimension: readonly QualityDimensionCount[];
  readonly views: readonly QualityViewSummary[];
}
